// src/components/PendingVerificationCard.tsx

import { useState, memo } from 'react';
import { CPItem } from './ContactPointsPanel';

interface PendingVerificationCardProps {
  item: CPItem;
  onVerify: (item: CPItem, code: string) => Promise<void>;
  onRemove: (item: CPItem) => Promise<void>;
  dmLinks?: Partial<Record<string, string>>;
  primaryEmail?: string;
  devInline?: boolean;
  onDevWebhookTest?: (item: CPItem) => Promise<void>;
}

const socialKinds = new Set(["instagram", "tiktok", "snapchat", "facebook", "linkedin"]);

/** Format phone for display */
function displayPhone(phone: string) {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    const n = digits.slice(1);
    return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

export const PendingVerificationCard = memo(function PendingVerificationCard({
  item,
  onVerify,
  onRemove,
  dmLinks,
  devInline,
  onDevWebhookTest,
}: PendingVerificationCardProps) {
    void ['PendingVerificationCard rendered:', { item, onVerify: typeof onVerify }];
  const [code, setCode] = useState('');
  const isSocial = socialKinds.has(item.kind);
  const isPhoneEmail = item.kind === "phone" || item.kind === "email";

  return (
    <div className="p-4 bg-white border-2 border-brand rounded-lg space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="font-medium text-gray-900">
            {item.kind === "phone" ? displayPhone(item.value) : item.value}
          </div>
          <div className="text-xs text-gray-500 capitalize mt-1">{item.kind}</div>
        </div>
        <button
          onClick={() => onRemove(item)}
          className="text-xs text-red-600 hover:text-red-700 hover:underline"
        >
          Remove
        </button>
      </div>

      {/* Phone/Email: Numeric OTP */}
      {isPhoneEmail && (
        <div className="space-y-2">
          <label className="block text-sm text-gray-700">
            Enter the 6-digit code we sent you:
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              className="flex-1 border rounded-lg p-2 text-center font-mono"
              placeholder="123456"
              value={code}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                setCode(val);
              }}
            />
          <button
            onClick={() => {
              void ['VERIFY BUTTON CLICKED!', { item, code }]; // ← ADD THIS
              onVerify(item, code);
            }}
            className="px-4 py-2 bg-brand text-white rounded-lg font-medium hover:bg-branddark"
          >
            Verify
          </button>
          </div>
        </div>
      )}

      {/* Social: Alphanumeric code + DM reminder */}
      {isSocial && (
        <div className="space-y-3">
          <div className="p-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-700">
            <p className="font-semibold mb-1">Verification Code:</p>
            <p className="font-mono text-lg text-brand">{item.code}</p>
            <p className="text-xs text-gray-600 mt-2">
              DM this code to{" "}
              <a
                href={dmLinks?.[item.kind] || "#"}
                target="_blank"
                rel="noreferrer"
                className="text-brand hover:underline"
              >
                {dmLinks?.[item.kind] || "@crushhh"}
              </a>{" "}
              on {item.kind} to verify you own this handle.
            </p>
          </div>

          {/* DEV MODE: Webhook test */}
          {devInline && onDevWebhookTest && (
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-xs text-purple-700 mb-2">
                <strong>DEV MODE:</strong> Simulate webhook verification
              </p>
              <button
                onClick={() => onDevWebhookTest(item)}
                className="text-xs px-3 py-1 bg-purple-100 rounded hover:bg-purple-200"
              >
                Simulate Webhook
              </button>
            </div>
          )}

          {/* Manual code entry */}
          <div className="space-y-2">
            <label className="block text-sm text-gray-700">
              Or enter the code here if you sent it:
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                maxLength={6}
                className="flex-1 border rounded-lg p-2 text-center font-mono uppercase"
                placeholder="ABC123"
                value={code}
                onChange={(e) => {
                  const val = e.target.value.toUpperCase().slice(0, 6);
                  setCode(val);
                }}
              />
              <button
                onClick={() => onVerify(item, code)}
                className="px-4 py-2 bg-brand text-white rounded-lg font-medium hover:bg-branddark"
              >
                Verify
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});